import { useState, useEffect } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import PropTypes from "prop-types";

/**
 * A form field component for autocomplete selection integrated with react-hook-form.
 * Supports loading options from an API and dependent fields.
 *
 * @param {string} name - The name of the field in the form.
 * @param {string} label - The label for the field.
 * @param {Array} options - The options for the autocomplete.
 * @param {function} getOptionLabel - Function to get the label for an option.
 * @param {function} onChange - Optional callback when the value changes.
 * @param {string} dependsOn - Optional field name that this field depends on.
 * @param {function} fetchOptions - Optional function to fetch options from an API.
 * @param {boolean} clearWhenDisabled - Whether to clear the field value when it becomes disabled.
 * @param {boolean} clearWhenDependencyChanges - Whether to clear the field value when the dependency changes.
 * @param {function} transformValue - Optional function to transform the selected option before storing in form (e.g., option => option.id).
 * @param {function} findOptionByValue - Optional function to find the option object from the stored value (e.g., value => options.find(opt => opt.id === value)).
 * @param {object} otherProps - Other props to pass to the MUI Autocomplete.
 */
const FormAutocomplete = ({
  name,
  label,
  options: propOptions,
  getOptionLabel = (option) => option?.label || "",
  findOptionByValue = (value, options) => {
    const findById = (val) => options.find((opt) => opt.id === val) || null;
    const resolveSingle = (val) => {
      if (val == null) {
        return null;
      }
      // If value is already an option-like object with id (and maybe label), try to match by id
      if (typeof val === "object") {
        const id = val?.id ?? val?.key;
        if (id != null) {
          return findById(id) || val; // fallback to the provided object so MUI can display its label
        }
        return null;
      }
      // Primitive (id)
      return findById(val);
    };

    if (Array.isArray(value)) {
      return value.map((v) => resolveSingle(v)).filter(Boolean);
    }
    return resolveSingle(value);
  },
  transformValue = (option) => option?.id || null,
  onChange,
  dependsOn,
  fetchOptions,
  clearWhenDisabled = false,
  clearWhenDependencyChanges = false,
  ...otherProps
}) => {
  const { control, setValue } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, defaultValue: null });

  const [options, setOptions] = useState(propOptions || []);
  const [loading, setLoading] = useState(false);
  const isMultiple = otherProps.multiple || false;
  const [previousDisabledState, setPreviousDisabledState] = useState(
    otherProps.disabled
  );
  const [previousDependentValue, setPreviousDependentValue] = useState(null);

  // Always call useWatch, but only use the value if dependsOn is provided
  const watchedValue = useWatch({
    control,
    name: dependsOn || name, // Watch self if no dependency specified
  });

  // Get the actual dependent value only if dependsOn is provided
  const dependentValue = dependsOn ? watchedValue : null;

  // Get the display value (option object) from the stored value
  const getDisplayValue = () => {
    if (!field.value) {
      return isMultiple ? [] : null;
    }

    if (isMultiple) {
      if (Array.isArray(field.value)) {
        return field.value
          .map((val) =>
            findOptionByValue ? findOptionByValue(val, options) : val
          )
          .filter(Boolean);
      }
      return [];
    }

    if (findOptionByValue) {
      return findOptionByValue(field.value, options) || null;
    }

    return field.value || null;
  };

  // Effect to clear field when it becomes disabled
  useEffect(() => {
    if (clearWhenDisabled && otherProps.disabled && !previousDisabledState) {
      setValue(name, isMultiple ? [] : null);
    }
    setPreviousDisabledState(otherProps.disabled);
  }, [
    otherProps.disabled,
    clearWhenDisabled,
    setValue,
    name,
    previousDisabledState,
  ]);

  // Effect to clear field when dependency changes
  useEffect(() => {
    if (
      clearWhenDependencyChanges &&
      dependsOn &&
      dependentValue !== previousDependentValue
    ) {
      // Only clear if this is not the initial load (previousDependentValue is not null)
      if (previousDependentValue !== null) {
        setValue(name, null);
      }
      setPreviousDependentValue(dependentValue);
    }
  }, [
    dependentValue,
    clearWhenDependencyChanges,
    dependsOn,
    setValue,
    name,
    previousDependentValue,
  ]);

  // Effect to fetch options when dependent field changes
  useEffect(() => {
    if (!fetchOptions) {
      return;
    }

    let active = true;

    const loadOptions = async () => {
      setLoading(true);
      try {
        // Pass the dependent value to fetchOptions
        const fetchedOptions = await fetchOptions(dependentValue);
        if (active) {
          setOptions(fetchedOptions || []);
        }
      } catch (error) {
        if (active) {
          setOptions([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadOptions();

    return () => {
      active = false;
    };
  }, [fetchOptions, dependentValue]);

  // Update options when propOptions changes
  useEffect(() => {
    if (propOptions) {
      setOptions(propOptions);
    }
  }, [propOptions]);

  // Handle value change
  const handleChange = (event, newValue) => {
    // Transform the value if a transform function is provided
    let valueToStore;

    if (isMultiple) {
      valueToStore = Array.isArray(newValue)
        ? newValue.map((option) =>
            transformValue ? transformValue(option) : option
          )
        : [];
    } else {
      valueToStore = transformValue ? transformValue(newValue) : newValue;
    }
    field.onChange(valueToStore);

    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <Autocomplete
      {...field}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionKey={(option) =>
        option?.id || option?.key || JSON.stringify(option)
      }
      value={getDisplayValue()}
      onChange={handleChange}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={error?.message}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      {...otherProps}
    />
  );
};

FormAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array,
  getOptionLabel: PropTypes.func,
  onChange: PropTypes.func,
  dependsOn: PropTypes.string,
  fetchOptions: PropTypes.func,
  clearWhenDisabled: PropTypes.bool,
  clearWhenDependencyChanges: PropTypes.bool,
  transformValue: PropTypes.func,
  findOptionByValue: PropTypes.func,
};

export default FormAutocomplete;
